import { useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Form, Input, Flex, Typography, Spin, Avatar } from "antd";

import ChevronRightIcon from "@/icons/ChevronRightIcon";
import { ArrowLeftOutlined } from "@ant-design/icons";

import styles from "./login.module.css";

import { SocketContext } from "@/context/SocketContext";

import { login, recover, getMe, logout } from "@api/auth";
import { prewarmMessengerBootstrap } from "@/api/messenger";
import { getItem as getTask } from "@/api/tasks";
import { getItem as getTaskTemplate } from "@/api/task-templates";
import { getObject } from "@/api/objects";
import { getItem as getSprint } from "@/api/sprints";
import {
  LOGIN_REDIRECT_PARAM,
  getRedirectPathForUser,
  getSafeRedirectPath,
} from "@/utils/authRedirect";

const { Text } = Typography;
const { TextArea } = Input;

const getRedirectAccessCheck = (redirectPath) => {
  const { pathname } = new URL(redirectPath, window.location.origin);
  const isAdminPath = pathname.startsWith("/administration");

  const adminTaskMatch = pathname.match(
    /^\/administration\/all-tasks\/all-t-list\/([^/]+)$/
  );
  const taskMatch =
    adminTaskMatch ||
    pathname.match(/^\/tasks\/t-list\/([^/]+)$/) ||
    pathname.match(/^\/units\/[^/]+\/u-tasks\/u-tasks-list\/([^/]+)$/);

  if (taskMatch) {
    return () => getTask(taskMatch[1], isAdminPath);
  }

  const adminTaskTemplateMatch = pathname.match(
    /^\/administration\/all-tasks\/all-t-templates\/([^/]+)$/
  );
  const taskTemplateMatch =
    adminTaskTemplateMatch ||
    pathname.match(/^\/tasks\/t-templates\/([^/]+)$/) ||
    pathname.match(/^\/units\/[^/]+\/u-tasks\/u-tasks-templates\/([^/]+)$/);

  if (taskTemplateMatch) {
    return () => getTaskTemplate(taskTemplateMatch[1], isAdminPath);
  }

  const adminSprintMatch = pathname.match(
    /^\/administration\/all-sprints\/([^/]+)$/
  );
  const sprintMatch =
    adminSprintMatch ||
    pathname.match(/^\/sprints\/([^/]+)$/) ||
    pathname.match(/^\/units\/[^/]+\/u-sprints\/([^/]+)$/);

  if (sprintMatch) {
    return () => getSprint(sprintMatch[1], isAdminPath);
  }

  const adminObjectMatch = pathname.match(
    /^\/administration\/all-objects\/([^/]+)\/([^/]+)$/
  );
  const objectMatch =
    adminObjectMatch ||
    pathname.match(/^\/my-objects\/([^/]+)\/([^/]+)$/) ||
    pathname.match(/^\/units\/[^/]+\/u-objects\/([^/]+)\/([^/]+)$/);

  if (objectMatch) {
    return () => getObject(objectMatch[1], objectMatch[2], isAdminPath);
  }

  return null;
};

// Форма логина и восстановления пароля
// При успешном логине перенаправляет на главную страницу
// При восстановлении пароля отправляет сообщение админу
// Есть переключатель между формами (Забыли пароль?)
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { reconnect } = useContext(SocketContext);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [isRecover, setIsRecover] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const redirectPath = getSafeRedirectPath(
    searchParams.get(LOGIN_REDIRECT_PARAM) || location.state?.from
  );
  const navigateAfterLogin = async (nextUser, options) => {
    const nextPath = getRedirectPathForUser(redirectPath, nextUser, options);
    const checkAccess = getRedirectAccessCheck(nextPath);

    if (checkAccess) {
      try {
        await checkAccess();
      } catch (error) {
        console.error("Redirect target is not available:", error);
        navigate("/", { replace: true });
        return;
      }
    }

    navigate(nextPath, { replace: true });
  };

  // Проверка авторизации при загрузке страницы
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { user } = await getMe();

        if (user) {
          setUser(user);
        }
      } catch (error) {
        console.error("Ошибка проверки авторизации:", error);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Отправка формы логина
  const handleLogin = async (values) => {
    setPending(true);
    setError(null);

    try {
      // Проверяем есть ли уже активная сессия
      const { user } = await getMe();

      if (user?.email === values.email) {
        // Если getMe вернуло того же пользователя, что пытается залогиниться
        await navigateAfterLogin(user);

        return;
      } else {
        await logout();
        const loginResponse = await login(values.email, values.password);

        if (reconnect) {
          setTimeout(() => reconnect(), 300);
        }

        await navigateAfterLogin(loginResponse?.user, {
          preserveRequestedPath: false,
        });
        return;
      }
    } catch (sessionError) {
      // Если getMe вернуло ошибку, значит нет активной сессии
      try {
        const loginResponse = await login(values.email, values.password);
        let nextUser = loginResponse?.user;

        if (!nextUser) {
          try {
            nextUser = (await getMe())?.user;
          } catch (error) {
            console.error("Ошибка получения пользователя после входа:", error);
          }
        }

        prewarmMessengerBootstrap();

        // Переподключаем сокет после успешного логина
        if (reconnect) {
          setTimeout(() => reconnect(), 300);
        }

        await navigateAfterLogin(nextUser);
        return;
      } catch (loginError) {
        setError(loginError.message);
      }
    } finally {
      setPending(false);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await logout();
      setUser(null);
    } catch (err) {
      console.log("Error", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Отправка формы восстановления пароля (сообщение админу)
  const handleSendMail = async (values) => {
    setPending(true);
    setError(null);

    try {
      await recover(values.email, values.message);
      setIsSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setPending(false);
    }
  };

  // Переключить форму на восстановление пароля
  // const handleRecover = (e) => {
  //   e.preventDefault();
  //   setError(null);
  //   setIsRecover(true);
  // };

  // Переключить форму на логин обратно
  const handleBack = (e) => {
    e.preventDefault();
    setError(null);
    setIsRecover(false);
    setIsSent(false);
  };

  return (
    <Spin spinning={isLoading}>
      {user ? (
        <Flex vertical gap={35} align="center">
          <Flex vertical gap={10} align="center">
            <Text>В системе уже авторизован пользователь</Text>
            <Flex gap={6} align="center">
              <Avatar
                src={user?.avatar || null}
                size={24}
                style={{
                  color: "#E66A25",
                  background: "#FFEADE",
                  fontWeight: 500,
                  border: "none",
                  textTransform: "uppercase",
                }}
              >
                {user?.lastname?.[0] || ""}
                {user?.firstname?.[0] || ""}
              </Avatar>

              {user?.email}
            </Flex>
          </Flex>
          <Flex
            justify="space-between"
            align="center"
            gap={10}
            style={{ width: "100%" }}
          >
            <Button onClick={handleLogout}>
              Войти как другой пользователь
            </Button>
            <Button
              type="primary"
              onClick={() => navigateAfterLogin(user)}
            >
              Продолжить
              <ChevronRightIcon />
            </Button>
          </Flex>
        </Flex>
      ) : (
        <>
          {!isRecover ? (
            <Form
              name="loginForm"
              layout="vertical"
              requiredMark={"optional"}
              size="large"
              className={styles.form}
              onFinish={handleLogin}
            >
              <Form.Item
                label="Логин"
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Введите ваш логин",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Пароль"
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Введите ваш пароль",
                  },
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item noStyle>
                <Flex justify="space-between" align="center">
                  <Button
                    loading={pending}
                    type="primary"
                    htmlType="submit"
                    style={{ minWidth: 110 }}
                  >
                    Войти
                  </Button>
                  {/* <Typography.Link onClick={handleRecover}>
                Забыли пароль?
              </Typography.Link> */}
                </Flex>
              </Form.Item>

              {error && (
                <Text type="danger" className={styles.error}>
                  {error}
                </Text>
              )}
            </Form>
          ) : !isSent ? (
            <Form
              name="recoverForm"
              layout="vertical"
              requiredMark="default"
              size="large"
              className={styles.form}
              onFinish={handleSendMail}
            >
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Введите ваш email",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item label="Сообщение" name="message">
                <TextArea
                  rows={4}
                  placeholder="Напишите сообщение администратору"
                />
              </Form.Item>

              <Form.Item noStyle>
                <Flex justify="space-between" align="center">
                  <Button
                    loading={pending}
                    type="primary"
                    htmlType="submit"
                    style={{ minWidth: 110 }}
                  >
                    Отправить обращение
                  </Button>
                  <Typography.Link onClick={handleBack}>
                    <ArrowLeftOutlined /> Назад
                  </Typography.Link>
                </Flex>
              </Form.Item>

              {error && (
                <Text type="danger" className={styles.error}>
                  {error}
                </Text>
              )}
            </Form>
          ) : (
            <Flex vertical gap={20} align="center">
              <Text className={styles.success} align="center">
                Сообщение отправлено. Администратор свяжется с&nbsp;вами
                в&nbsp;ближайшее время.
              </Text>
              <Button
                type="primary"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
              >
                Назад
              </Button>
            </Flex>
          )}
        </>
      )}
    </Spin>
  );
};

export default Login;
